import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import axios from "../AxiosUrl";
import "./Sidebar.css";

// Marketing and AI Calling pages (Docs/SMS_MARKETING_CONVERSION_TRACKING_PLAN.md)
const MARKETING_ITEMS = [
  ["/marketing/summary", "insights", "Results"],
  ["/marketing/reminders", "notifications_active", "Reminders"],
  ["/marketing/automations", "schedule_send", "Automations"],
  ["/marketing/settings", "tune", "Settings"],
];
const AI_ITEMS = [
  ["/ai-calling", "support_agent", "Control"],
  ["/ai-calling/live", "call", "Live calls"],
  ["/ai-calling/campaigns", "campaign", "Campaigns"],
  ["/ai-calling/calls", "history", "Call history"],
  ["/ai-calling/reports", "bar_chart", "Reports"],
  ["/ai-calling/ticket-agent", "manage_search", "Ticket agent"],
  ["/ai-calling/dnc", "phone_disabled", "Do-not-call"],
  ["/ai-calling/settings", "settings_voice", "Settings"],
  ["/ai-calling/sip", "dialpad", "SIP line"],
];
// Defaults of Marketing settings → Access, used until the real lists load
const DEFAULT_AREA_ROLES = { campaigns: ["Supper", "Admin", "Team"], aiCalling: ["Supper", "Admin"] };

function Sidebar({ isSidebarOpen }) {
  const [showAnalyticsSubMenu, setShowAnalyticsSubMenu] = useState(false);
  const [openGroups, setOpenGroups] = useState({ marketing: false, ai: false });
  const [isSidebarHovered, setIsSidebarHovered] = useState(false);
  const location = useLocation();

  // Get current user's role
  const AdminType = localStorage.getItem("AdminType");

  // Which marketing areas this admin may use (Marketing settings → Access)
  const [areaAccess, setAreaAccess] = useState(() => ({
    campaigns: DEFAULT_AREA_ROLES.campaigns.includes(AdminType),
    aiCalling: DEFAULT_AREA_ROLES.aiCalling.includes(AdminType),
  }));
  useEffect(() => {
    const uid = localStorage.getItem("id");
    if (!uid) return;
    axios
      .get(`/marketing/settings/${uid}`)
      .then((res) => {
        if (res.data?.success && res.data.can) {
          setAreaAccess({ campaigns: !!res.data.can.campaigns, aiCalling: !!res.data.can.aiCalling });
        }
      })
      .catch(() => {});
  }, []);
  const toggleGroup = (key) => setOpenGroups((g) => ({ ...g, [key]: !g[key] }));

  // Helper function to check if menu item should be visible based on role
  const canAccess = (allowedRoles) => {
    if (!allowedRoles || allowedRoles.length === 0) return true;
    return allowedRoles.includes(AdminType);
  };

  const handleAnalyticsClick = () => {
    setShowAnalyticsSubMenu(!showAnalyticsSubMenu);
  };

  // Close submenu when sidebar is collapsed
  useEffect(() => {
    if (!isSidebarOpen) {
      setShowAnalyticsSubMenu(false);
      setOpenGroups({ marketing: false, ai: false });
    }
  }, [isSidebarOpen]);

  // Close dropdown when sidebar loses hover (for desktop hover behavior)
  useEffect(() => {
    // On desktop, if sidebar is not open and loses hover, close dropdown
    if (!isSidebarHovered && !isSidebarOpen) {
      const timer = setTimeout(() => {
        setShowAnalyticsSubMenu(false);
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [isSidebarHovered, isSidebarOpen]);

  const handleSidebarMouseEnter = () => {
    setIsSidebarHovered(true);
  };

  const handleSidebarMouseLeave = () => {
    setIsSidebarHovered(false);
  };

  // Determine if a menu item is active
  const isActive = (path) => {
    return location.pathname === path;
  };

  // Animation variants
  const menuItemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (custom) => ({
      opacity: 1,
      x: 0,
      transition: { delay: 0.05 * custom, duration: 0.3 }
    })
  };

  // Container animation for the submenu - improved for smoother animation
  const subMenuVariants = {
    hidden: { 
      height: 0,
      opacity: 0,
      transition: {
        height: {
          duration: 0.25,
          ease: [0.4, 0, 0.2, 1]
        },
        opacity: {
          duration: 0.2,
          ease: [0.4, 0, 0.2, 1]
        },
        when: "afterChildren",
        staggerChildren: 0.03,
        staggerDirection: -1
      }
    },
    visible: { 
      height: "auto",
      opacity: 1,
      transition: {
        height: {
          duration: 0.3,
          ease: [0.4, 0, 0.2, 1]
        },
        opacity: {
          duration: 0.25,
          ease: [0.4, 0, 0.2, 1],
          delay: 0.05
        },
        when: "beforeChildren",
        staggerChildren: 0.05,
        delayChildren: 0.05
      }
    }
  };

  // Wave animation for each submenu item - improved timing
  const subMenuItemVariants = {
    hidden: { 
      x: -15,
      opacity: 0,
      scale: 0.95,
      transition: { 
        duration: 0.2,
        ease: [0.4, 0, 0.2, 1]
      }
    },
    visible: (custom) => ({ 
      x: 0,
      opacity: 1,
      scale: 1,
      transition: { 
        duration: 0.3,
        ease: [0.4, 0, 0.2, 1],
        delay: custom * 0.05
      }
    })
  };

  // A menu group with its own submenu (same look as Analytics)
  const renderGroup = (key, icon, label, items, custom) => (
    <>
      <motion.li
        className={`menu-item ${items.some(([to]) => isActive(to)) ? "active" : ""}`}
        onClick={() => toggleGroup(key)}
        variants={menuItemVariants}
        initial="hidden"
        animate="visible"
        custom={custom}
      >
        <div className="menu-link">
          <span className="material-symbols-outlined">{icon}</span>
          <span>{label}</span>
          <motion.span
            className="material-icons arrow-icon"
            animate={{ rotate: openGroups[key] ? 90 : 0, transition: { type: "spring", stiffness: 260, damping: 20 } }}
          >
            chevron_right
          </motion.span>
        </div>
      </motion.li>
      <AnimatePresence initial={false} mode="wait">
        {openGroups[key] && (isSidebarOpen || isSidebarHovered) && (
          <motion.ul className="sub-menu" variants={subMenuVariants} initial="hidden" animate="visible" exit="hidden" layout>
            {items.map(([to, itemIcon, text], i) => (
              <motion.li key={to} className={`sub-menu-item ${isActive(to) ? "active" : ""}`} variants={subMenuItemVariants} custom={i + 1}>
                <Link to={to} className="menu-link">
                  <span className="material-icons icon-sub">{itemIcon}</span>
                  <span>{text}</span>
                </Link>
              </motion.li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </>
  );

  return (
    <div 
      className={`sidebar ${isSidebarOpen ? 'open' : ''}`}
      onMouseEnter={handleSidebarMouseEnter}
      onMouseLeave={handleSidebarMouseLeave}
    >
      <div className="sidebar-content">
        <ul className="sidebar-menu">
          {/* Dashboard - Supper, Admin, Team only */}
          {canAccess(["Supper", "Admin", "Team"]) && (
            <motion.li
              className={`menu-item ${isActive('/dashboard') ? 'active' : ''}`}
              variants={menuItemVariants}
              initial="hidden"
              animate="visible"
              custom={1}
            >
              <Link to="/dashboard" className="menu-link">
                <span className="material-icons icon-home">home</span>
                <span>Dashboard</span>
              </Link>
            </motion.li>
          )}

          {/* Profiles - All roles */}
          <motion.li
            className={`menu-item ${isActive('/users') ? 'active' : ''}`}
            variants={menuItemVariants}
            initial="hidden"
            animate="visible"
            custom={2}
          >
            <Link to="/users" className="menu-link">
              <span className="material-symbols-outlined">group</span>
              <span>Profiles</span>
            </Link>
          </motion.li>

          {/* User Management - All roles */}
          <motion.li
            className={`menu-item ${isActive('/user-management') ? 'active' : ''}`}
            variants={menuItemVariants}
            initial="hidden"
            animate="visible"
            custom={2.5}
          >
            <Link to="/user-management" className="menu-link">
              <span className="material-symbols-outlined">account_circle</span>
              <span>User Management</span>
            </Link>
          </motion.li>

          {/* Withdraw - Supper, Admin, Team only */}
          {canAccess(["Supper", "Admin", "Team"]) && (
            <motion.li
              className={`menu-item ${isActive('/withdraw') ? 'active' : ''}`}
              variants={menuItemVariants}
              initial="hidden"
              animate="visible"
              custom={3}
            >
              <Link to="/withdraw" className="menu-link">
                <span className="material-symbols-outlined">payments</span>
                <span>Withdraw</span>
              </Link>
            </motion.li>
          )}

          {/* Bkash Payments - All roles */}
          <motion.li
            className={`menu-item ${isActive('/payments') ? 'active' : ''}`}
            variants={menuItemVariants}
            initial="hidden"
            animate="visible"
            custom={4}
          >
            <Link to="/payments" className="menu-link">
              <span className="material-symbols-outlined">payment</span>
              <span>Bkash Payments</span>
            </Link>
          </motion.li>

          {/* Packages - Supper, Admin, Team only */}
          {canAccess(["Supper", "Admin", "Team"]) && (
            <motion.li
              className={`menu-item ${isActive('/packages') ? 'active' : ''}`}
              variants={menuItemVariants}
              initial="hidden"
              animate="visible"
              custom={5}
            >
              <Link to="/packages" className="menu-link">
                <span className="material-symbols-outlined">inventory_2</span>
                <span>Packages</span>
              </Link>
            </motion.li>
          )}

          {/* Wallet coupons - Supper, Admin only (they give money away) */}
          {canAccess(["Supper", "Admin"]) && (
            <motion.li
              className={`menu-item ${isActive('/coupons') ? 'active' : ''}`}
              variants={menuItemVariants}
              initial="hidden"
              animate="visible"
              custom={5.5}
            >
              <Link to="/coupons" className="menu-link">
                <span className="material-symbols-outlined">confirmation_number</span>
                <span>Coupons</span>
              </Link>
            </motion.li>
          )}

          {/* Campaigns - Supper, Admin, Team only */}
          {canAccess(["Supper", "Admin", "Team"]) && (
            <motion.li
              className={`menu-item ${isActive('/campaigns') ? 'active' : ''}`}
              variants={menuItemVariants}
              initial="hidden"
              animate="visible"
              custom={6}
            >
              <Link to="/campaigns" className="menu-link">
                <span className="material-symbols-outlined">campaign</span>
                <span>Campaigns</span>
              </Link>
            </motion.li>
          )}

          {/* Marketing - results, reminders, automations, settings */}
          {areaAccess.campaigns && renderGroup("marketing", "insights", "Marketing", MARKETING_ITEMS, 6)}

          {/* AI Calling - roles from Marketing settings → Access (default Supper, Admin) */}
          {areaAccess.aiCalling && renderGroup("ai", "support_agent", "AI Calling", AI_ITEMS, 6)}

          {/* Support Ticket - Supper, Admin, Team only */}
          {canAccess(["Supper", "Admin", "Team"]) && (
            <motion.li
              className={`menu-item ${isActive('/support-ticket') ? 'active' : ''}`}
              variants={menuItemVariants}
              initial="hidden"
              animate="visible"
              custom={7}
            >
              <Link to="/support-ticket" className="menu-link">
                <span className="material-symbols-outlined">confirmation_number</span>
                <span>Support Ticket</span>
              </Link>
            </motion.li>
          )}

          {/* User Reports - Supper, Admin, Team only */}
          {canAccess(["Supper", "Admin", "Team"]) && (
            <motion.li
              className={`menu-item ${isActive('/user-reports') ? 'active' : ''}`}
              variants={menuItemVariants}
              initial="hidden"
              animate="visible"
              custom={7}
            >
              <Link to="/user-reports" className="menu-link">
                <span className="material-symbols-outlined">flag</span>
                <span>User Reports</span>
              </Link>
            </motion.li>
          )}

          {/* App Updates - Supper, Admin only */}
          {canAccess(["Supper", "Admin"]) && (
            <motion.li
              className={`menu-item ${isActive('/update-settings') ? 'active' : ''}`}
              variants={menuItemVariants}
              initial="hidden"
              animate="visible"
              custom={8}
            >
              <Link to="/update-settings" className="menu-link">
                <span className="material-symbols-outlined">system_update</span>
                <span>App Updates</span>
              </Link>
            </motion.li>
          )}

          {/* Redis Cache - Supper, Admin only */}
          {canAccess(["Supper", "Admin"]) && (
            <motion.li
              className={`menu-item ${isActive('/redis-management') ? 'active' : ''}`}
              variants={menuItemVariants}
              initial="hidden"
              animate="visible"
              custom={9}
            >
              <Link to="/redis-management" className="menu-link">
                <span className="material-symbols-outlined">memory</span>
                <span>Redis Cache</span>
              </Link>
            </motion.li>
          )}

          {/* System Logs - Supper, Admin only */}
          {canAccess(["Supper", "Admin"]) && (
            <motion.li
              className={`menu-item ${isActive('/logs') ? 'active' : ''}`}
              variants={menuItemVariants}
              initial="hidden"
              animate="visible"
              custom={10}
            >
              <Link to="/logs" className="menu-link">
                <span className="material-symbols-outlined">description</span>
                <span>System Logs</span>
              </Link>
            </motion.li>
          )}

          {/* Crash Logs - Supper, Admin only */}
          {canAccess(["Supper", "Admin"]) && (
            <motion.li
              className={`menu-item ${isActive('/crash-logs') ? 'active' : ''}`}
              variants={menuItemVariants}
              initial="hidden"
              animate="visible"
              custom={11}
            >
              <Link to="/crash-logs" className="menu-link">
                <span className="material-symbols-outlined">bug_report</span>
                <span>Crash Logs</span>
              </Link>
            </motion.li>
          )}

          {/* Payment Errors - Supper, Admin only */}
          {canAccess(["Supper", "Admin"]) && (
            <motion.li
              className={`menu-item ${isActive('/payment-errors') ? 'active' : ''}`}
              variants={menuItemVariants}
              initial="hidden"
              animate="visible"
              custom={12}
            >
              <Link to="/payment-errors" className="menu-link">
                <span className="material-symbols-outlined">credit_card_off</span>
                <span>Payment Errors</span>
              </Link>
            </motion.li>
          )}

          {/* Attack Monitor - Supper, Admin only */}
          {canAccess(["Supper", "Admin"]) && (
            <motion.li
              className={`menu-item ${isActive('/attack') ? 'active' : ''}`}
              variants={menuItemVariants}
              initial="hidden"
              animate="visible"
              custom={12}
            >
              <Link to="/attack" className="menu-link">
                <span className="material-symbols-outlined">shield</span>
                <span>Attack</span>
              </Link>
            </motion.li>
          )}

          {/* Health Check - Supper, Admin only */}
          {canAccess(["Supper", "Admin"]) && (
            <motion.li
              className={`menu-item ${isActive('/health-check') ? 'active' : ''}`}
              variants={menuItemVariants}
              initial="hidden"
              animate="visible"
              custom={13}
            >
              <Link to="/health-check" className="menu-link">
                <span className="material-symbols-outlined">monitor_heart</span>
                <span>Health Check</span>
              </Link>
            </motion.li>
          )}

          {/* Analytics - Supper, Admin, Team only */}
          {canAccess(["Supper", "Admin", "Team"]) && (
            <motion.li
              className="menu-item"
              onClick={handleAnalyticsClick}
              variants={menuItemVariants}
              initial="hidden"
              animate="visible"
              custom={14}
            >
              <div className="menu-link">
                <span className="material-icons icon-analytics">analytics</span>
                <span>Analytics</span>
                <motion.span
                  className="material-icons arrow-icon"
                  animate={{
                    rotate: showAnalyticsSubMenu ? 90 : 0,
                    transition: {
                      type: "spring",
                      stiffness: 260,
                      damping: 20
                    }
                  }}
                >
                  chevron_right
                </motion.span>
              </div>
            </motion.li>
          )}

          {/* Submenu with wave animation - Supper, Admin, Team only */}
          {canAccess(["Supper", "Admin", "Team"]) && (
            <AnimatePresence initial={false} mode="wait">
              {showAnalyticsSubMenu && (isSidebarOpen || isSidebarHovered) && (
                <motion.ul 
                  className="sub-menu"
                  variants={subMenuVariants}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  layout
                >
                  <motion.li 
                    className="sub-menu-item"
                    variants={subMenuItemVariants}
                    custom={1}
                  >
                    <Link to="/user-progress" className="menu-link">
                      <span className="material-icons icon-sub">person</span>
                      <span>User Progress</span>
                    </Link>
                  </motion.li>
                  <motion.li 
                    className="sub-menu-item"
                    variants={subMenuItemVariants}
                    custom={2}
                  >
                    <Link to="/orders-revenue" className="menu-link">
                      <span className="material-icons icon-sub">attach_money</span>
                      <span>Orders & Revenue</span>
                    </Link>
                  </motion.li>
                  <motion.li 
                    className="sub-menu-item"
                    variants={subMenuItemVariants}
                    custom={3}
                  >
                    <Link to="/signups" className="menu-link">
                      <span className="material-icons icon-sub">group_add</span>
                      <span>Signups</span>
                    </Link>
                  </motion.li>
                  <motion.li 
                    className="sub-menu-item"
                    variants={subMenuItemVariants}
                    custom={4}
                  >
                    <Link to="/visitors" className="menu-link">
                      <span className="material-icons icon-sub">visibility</span>
                      <span>Visitors</span>
                    </Link>
                  </motion.li>
                  <motion.li 
                    className="sub-menu-item"
                    variants={subMenuItemVariants}
                    custom={5}
                  >
                    <Link to="/sms-analytics" className="menu-link">
                      <span className="material-icons icon-sub">sms</span>
                      <span>SMS Analytics</span>
                    </Link>
                  </motion.li>
                  <motion.li
                    className="sub-menu-item"
                    variants={subMenuItemVariants}
                    custom={6}
                  >
                    <Link to="/push-notifications" className="menu-link">
                      <span className="material-icons icon-sub">notifications</span>
                      <span>Push Notifications</span>
                    </Link>
                  </motion.li>
                  <motion.li
                    className={`sub-menu-item ${isActive('/communication-analytics') ? 'active' : ''}`}
                    variants={subMenuItemVariants}
                    custom={7}
                  >
                    <Link to="/communication-analytics" className="menu-link">
                      <span className="material-icons icon-sub">campaign</span>
                      <span>Communication</span>
                    </Link>
                  </motion.li>
                  <motion.li
                    className={`sub-menu-item ${isActive('/event-analytics') ? 'active' : ''}`}
                    variants={subMenuItemVariants}
                    custom={8}
                  >
                    <Link to="/event-analytics" className="menu-link">
                      <span className="material-icons icon-sub">bar_chart</span>
                      <span>Events</span>
                    </Link>
                  </motion.li>
                  <motion.li
                    className={`sub-menu-item ${isActive('/attribution-analytics') ? 'active' : ''}`}
                    variants={subMenuItemVariants}
                    custom={9}
                  >
                    <Link to="/attribution-analytics" className="menu-link">
                      <span className="material-icons icon-sub">ads_click</span>
                      <span>Attribution</span>
                    </Link>
                  </motion.li>
                  <motion.li
                    className={`sub-menu-item ${isActive('/funnel-analytics') ? 'active' : ''}`}
                    variants={subMenuItemVariants}
                    custom={10}
                  >
                    <Link to="/funnel-analytics" className="menu-link">
                      <span className="material-icons icon-sub">filter_alt</span>
                      <span>Funnel</span>
                    </Link>
                  </motion.li>
                </motion.ul>
              )}
            </AnimatePresence>
          )}

          {/* App Config - Supper, Admin only */}
          {canAccess(["Supper", "Admin"]) && (
            <motion.li
              className={`menu-item ${isActive('/app-config') ? 'active' : ''}`}
              variants={menuItemVariants}
              initial="hidden"
              animate="visible"
              custom={15}
            >
              <Link to="/app-config" className="menu-link">
                <span className="material-symbols-outlined">phone_android</span>
                <span>App Config</span>
              </Link>
            </motion.li>
          )}

          {/* Settings - All roles (but non-clickable, handled elsewhere) */}
          <motion.li
            className="menu-item"
            variants={menuItemVariants}
            initial="hidden"
            animate="visible"
            custom={16}
          >
            <div className="menu-link">
              <span className="material-icons icon-settings">settings</span>
              <span>Settings</span>
            </div>
          </motion.li>
        </ul>
      </div>
    </div>
  );
}

export default Sidebar;
